import { Format, jObjectLittle, JUserApi, jUserView, UserField } from 'jamespot-user-api';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { JRCStyledHref } from '../JRCHref/JRCHref';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import jamespot from 'jamespot-user-api';
import { useIntl } from 'react-intl';
import { JRCEllipsis } from '../JRCEllipsis/JRCEllipsis';
import { JRCUserStatusIcon } from '../JRCUserStatusIcon/JRCUserStatusIcon';

export type JRCUserPopupProps = {
    uri: string;
    fetchBusinessNetworking?: () => Promise<jObjectLittle>;
    onError?: (message?: string) => void;
    handlers: {
        user: Pick<JUserApi, 'get' | 'getFields'>;
    };
    children: ReactNode;
};

const HoverDiv = styled.div<{ open: boolean }>`
    display: flex;
    align-items: center;
    width: ${({ open }) => (open ? 'auto' : 'fit-content')};
`;

const PopupContainer = styled.div<{ open: boolean }>`
    display: ${({ open }) => (open ? 'block' : 'none')};
`;

const Popup = styled.div`
    display: flex;
    position: absolute;
    width: 360px;
    height: 220px;
    margin-top: -110px;
    margin-left: 22px;
    background-color: ${({ theme }) => theme.color.grey0};
    border: 2px solid ${({ theme }) => theme.color.grey3};
    align-items: center;
    border-radius: 8px;

    &:after {
        content: '';
        position: absolute;
        height: 26px;
        width: 26px;
        background-color: ${({ theme }) => theme.color.grey0};
        border-top-left-radius: 4px;
        transform: rotate(-45deg);
        margin-left: -13px;
        clip-path: polygon(0 0, 0 26px, 26px 0);
    }

    &:before {
        content: '';
        position: absolute;
        height: 30px;
        width: 30px;
        background-color: ${({ theme }) => theme.color.grey3};
        border-top-left-radius: 4px;
        transform: rotate(-45deg);
        margin-left: -15px;
        clip-path: polygon(0 0, 0 28px, 28px 0);
    }
`;

const Content = styled.div`
    z-index: 1;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
`;

const Header = styled.div`
    background-color: ${({ theme }) => theme.color.white};
    padding: 12px 12px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

// const ContactActions = styled.div`
//     display: flex;
//     gap: 8px;
//     align-items: center;
// `;

const Body = styled.div`
    background-color: ${({ theme }) => theme.color.white};
    padding: 6px;
    border-radius: 6px;
    height: 100%;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    overflow: hidden;
`;

const ProfileImgWrapper = styled.div`
    height: 98px;
    max-height: 100%;
    aspect-ratio: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.color.grey0};
    border-radius: 6px;
    overflow: hidden;
`;

const ProfileImg = styled.img`
    max-width: 100%;
    max-height: 100%;
`;

const Infos = styled.div`
    flex: 1;
`;

const InfoWrapper = styled.p`
    padding: 4px 8px;
    background-color: ${({ theme }) => theme.color.grey0};
    border-radius: 6px;
    display: inline-block;
    height: min-content;
    margin-right: 6px;
    margin-bottom: 6px;
`;

export const JRCUserPopup = ({ children, uri, fetchBusinessNetworking, handlers, onError }: JRCUserPopupProps) => {
    const intl = useIntl();
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout>();
    const [user, setUser] = useState<jUserView>();
    const [fields, setFields] = useState<{ name: string; value: string | object }[]>();
    const [extendedFields, setExtendedFields] = useState<Array<{ ref: string; display: string | React.ReactNode }>>([]);

    const _fetchBusinessNetworking = useMemo(
        () =>
            fetchBusinessNetworking ?? (async (id: number) => (await jamespot.businessNetworking.getInfos(id)).result),
        [fetchBusinessNetworking],
    );

    const onMouseEnter = useCallback(() => {
        clearTimeout(hoverTimeout);
        if (!isPopupOpen) {
            setHoverTimeout(
                setTimeout(() => {
                    setPopupOpen(true);

                    handlers.user
                        .get<jUserView>(uri, Format.VIEW)
                        .then((res) => setUser(res.result))
                        .catch((e) => {
                            if (onError) {
                                onError(e.errorMsg);
                            }
                        });
                }, 1000),
            );
        }
    }, [handlers, uri, hoverTimeout, isPopupOpen, onError]);

    useEffect(() => {
        if (!user) {
            return;
        }
        handlers.user
            .getFields(user.id, 'popover')
            .then((res) => setFields(res.result))
            .catch((e) => {
                if (onError) {
                    onError(e.errorMsg);
                }
            });
    }, [user, handlers, onError]);

    const onMouseLeave = useCallback(() => {
        clearTimeout(hoverTimeout);
        if (isPopupOpen) {
            setHoverTimeout(
                setTimeout(() => {
                    setPopupOpen(false);
                    setUser(undefined);
                    setFields(undefined);
                }, 200),
            );
        }
    }, [hoverTimeout, isPopupOpen]);

    const formatOrFetchUserField = useCallback(
        (field: UserField) => {
            const { name, value } = field;
            if (typeof value === 'object') {
                switch (name) {
                    case 'businessNetworkingUserToCompanySrc':
                        Object.values(value).forEach((v) => {
                            if (!extendedFields.some((ef) => ef.ref === v.id)) {
                                _fetchBusinessNetworking(v.id).then((bn) => {
                                    setExtendedFields([
                                        ...extendedFields,
                                        {
                                            ref: v.id,
                                            display: <JRCStyledHref href={bn._url}>{bn.title}</JRCStyledHref>,
                                        },
                                    ]);
                                });
                            }
                        });
                        return null;
                    default:
                        return null;
                }
            } else {
                switch (name) {
                    case 'mail':
                        return <JRCStyledHref href={`mailto:${value}`}>{value}</JRCStyledHref>;
                    case 'dateOfBirth':
                        return intl.formatDate(value, {
                            year: '2-digit',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                    default:
                        return value;
                }
            }
        },
        [_fetchBusinessNetworking, extendedFields, intl],
    );

    return (
        <HoverDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} open={isPopupOpen}>
            {children}
            {user && fields && (
                <PopupContainer open={isPopupOpen}>
                    <Popup>
                        <Content>
                            <Header>
                                <JRCStyledHref href={user._url}>
                                    <JRCTypography size={'s'} weight={'bold'}>
                                        <JRCEllipsis length={35} label={user.title} />
                                        <JRCUserStatusIcon user={user} isUserActive={user.active === 1} />
                                    </JRCTypography>
                                </JRCStyledHref>
                                {/* TODO */}
                                {/*<ContactActions>*/}
                                {/*    <JRCIconButton icon={'icon-comment'} color={'grey3'} size={'small'} />*/}
                                {/*    <JRCIconButton icon={'icon-video-camera'} color={'grey3'} size={'small'} />*/}
                                {/*    <JRCIconButton icon={'icon-user-plus'} color={'grey3'} size={'small'} />*/}
                                {/*</ContactActions>*/}
                            </Header>
                            <Body>
                                <Infos>
                                    {fields
                                        .map((field) => formatOrFetchUserField(field))
                                        .filter((e) => !!e)
                                        .map((f, i) => (
                                            <InfoWrapper key={i}>
                                                <JRCTypography variant={'span'} size={'s'}>
                                                    {f}
                                                </JRCTypography>
                                            </InfoWrapper>
                                        ))}
                                    {extendedFields.map((f, i) => (
                                        <InfoWrapper key={i}>
                                            <JRCTypography variant={'span'} size={'s'}>
                                                {f.display}
                                            </JRCTypography>
                                        </InfoWrapper>
                                    ))}
                                </Infos>
                                <ProfileImgWrapper>
                                    <ProfileImg src={'/imagecache/160x160/' + user.uri} />
                                </ProfileImgWrapper>
                            </Body>
                        </Content>
                    </Popup>
                </PopupContainer>
            )}
        </HoverDiv>
    );
};
